import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { Plus, LogOut, Users, Columns3, Bell, User as UserIcon, Settings, Edit } from "lucide-react";
import ReminderNotifications from "../components/ReminderNotifications";

export default function Dashboard() {
  const [boards, setBoards] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingBoard, setEditingBoard] = useState(null);
  const [boardName, setBoardName] = useState("");
  const [isShared, setIsShared] = useState(false);
  const [sharedWith, setSharedWith] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem("user") || "{}");

  useEffect(() => {
    fetchBoards();
  }, []);

  const fetchBoards = async () => {
    try {
      const response = await axios.get("/boards");
      setBoards(response.data);
    } catch (error) {
      toast.error("Fehler beim Laden der Boards");
    } finally {
      setLoading(false);
    }
  };

  const createBoard = async () => {
    if (!boardName.trim()) {
      toast.error("Bitte geben Sie einen Board-Namen ein");
      return;
    }

    try {
      await axios.post("/boards", {
        name: boardName,
        isShared,
        sharedWith,
      });
      toast.success("Board erfolgreich erstellt!");
      closeDialogs();
      fetchBoards();
    } catch (error) {
      toast.error("Fehler beim Erstellen des Boards");
    }
  };

  const openEditDialog = (board, e) => {
    e.stopPropagation();
    setEditingBoard(board);
    setBoardName(board.name);
    setIsShared(board.isShared || false);
    setSharedWith(board.sharedWith || []);
    setShowEditDialog(true);
  };

  const updateBoard = async () => {
    if (!boardName.trim()) {
      toast.error("Bitte geben Sie einen Board-Namen ein");
      return;
    }

    try {
      await axios.put(`/boards/${editingBoard.id}`, {
        name: boardName,
        isShared,
        sharedWith,
      });
      toast.success("Board erfolgreich aktualisiert!");
      closeDialogs();
      fetchBoards();
    } catch (error) {
      toast.error("Fehler beim Aktualisieren des Boards");
    }
  };

  const closeDialogs = () => {
    setShowDialog(false);
    setShowEditDialog(false);
    setEditingBoard(null);
    setBoardName("");
    setIsShared(false);
    setSharedWith([]);
  };

  const handleLogout = () => {
    localStorage.removeItem("token");
    localStorage.removeItem("user");
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-purple-50">
      <ReminderNotifications />
      
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-gray-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-xl flex items-center justify-center shadow-md">
                <Columns3 className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Sat Nam Taskmanager</h1>
                <p className="text-sm text-gray-600">Willkommen, {user.username}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              {user.role === "admin" && (
                <>
                  <Button
                    data-testid="settings-button"
                    onClick={() => navigate("/admin/settings")}
                    variant="outline"
                    className="gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    Einstellungen
                  </Button>
                  <Button
                    data-testid="users-button"
                    onClick={() => navigate("/users")}
                    variant="outline"
                    className="gap-2"
                  >
                    <Users className="w-4 h-4" />
                    Benutzer
                  </Button>
                </>
              )}
              <button
                onClick={() => navigate("/profile")}
                className="focus:outline-none focus:ring-2 focus:ring-blue-500 rounded-full"
                title="Profil"
              >
                {user.avatarUrl ? (
                  <img
                    src={`${process.env.REACT_APP_BACKEND_URL}${user.avatarUrl}`}
                    alt={user.name || user.username}
                    className="w-10 h-10 rounded-full object-cover border-2 border-gray-200 hover:border-blue-400 transition-colors shadow-sm"
                  />
                ) : (
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center text-white font-semibold shadow-sm hover:shadow-md transition-shadow">
                    {(user.name || user.username).charAt(0).toUpperCase()}
                  </div>
                )}
              </button>
              <Button
                data-testid="logout-button"
                onClick={handleLogout}
                variant="outline"
                className="gap-2 text-red-600 hover:text-red-700"
              >
                <LogOut className="w-4 h-4" />
                Abmelden
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h2 className="text-3xl font-bold text-gray-900">Deine Boards</h2>
            <p className="text-gray-600 mt-1">Verwalte deine Aufgaben und Projekte</p>
          </div>
          <Dialog open={showDialog} onOpenChange={setShowDialog}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-board-button" 
                className="gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 shadow-lg"
              >
                <Plus className="w-4 h-4" />
                Neues Board
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="create-board-dialog" className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>Neues Board erstellen</DialogTitle>
                <DialogDescription>
                  Erstellen Sie ein neues Board für Ihre Projekte
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label htmlFor="board-name">Board-Name</Label>
                  <Input
                    id="board-name"
                    data-testid="board-name-input"
                    placeholder="z.B. Projekt X"
                    value={boardName}
                    onChange={(e) => setBoardName(e.target.value)}
                  />
                </div>
                
                <div className="space-y-3">
                  <Label>Sichtbarkeit</Label>
                  
                  {/* Private */}
                  <div
                    className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      !isShared && sharedWith.length === 0
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => {
                      setIsShared(false);
                      setSharedWith([]);
                    }}
                  >
                    <input
                      type="radio"
                      name="sharing"
                      checked={!isShared && sharedWith.length === 0}
                      onChange={() => {
                        setIsShared(false);
                        setSharedWith([]);
                      }}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Nur für mich</p>
                      <p className="text-sm text-gray-600">
                        Nur Du kannst dieses Board sehen und bearbeiten
                      </p>
                    </div>
                  </div>

                  {/* All Users */}
                  <div
                    className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      isShared
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => {
                      setIsShared(true);
                      setSharedWith([]);
                    }}
                  >
                    <input
                      type="radio"
                      name="sharing"
                      checked={isShared}
                      onChange={() => {
                        setIsShared(true);
                        setSharedWith([]);
                      }}
                      className="mt-1 mr-3"
                    />
                    <div>
                      <p className="font-medium text-gray-900">Für alle Benutzer</p>
                      <p className="text-sm text-gray-600">
                        Alle Benutzer können dieses Board sehen und bearbeiten
                      </p>
                    </div>
                  </div>

                  {/* Bestimmte Benutzer */}
                  <div
                    className={`flex flex-col p-3 border-2 rounded-lg cursor-pointer transition-all ${
                      !isShared && sharedWith.length > 0
                        ? "border-blue-500 bg-blue-50"
                        : "border-gray-200 hover:border-gray-300"
                    }`}
                    onClick={() => {
                      setIsShared(false);
                      setSharedWith([""]); 
                    }}
                  >
                    <label className="flex items-start cursor-pointer">
                      <input
                        type="radio"
                        name="sharing"
                        checked={!isShared && sharedWith.length > 0}
                        onChange={() => {
                          setIsShared(false);
                          setSharedWith([""]);
                        }}
                        className="mt-1 mr-3"
                      />
                      <div>
                        <p className="font-medium text-gray-900">Teilen mit bestimmten Benutzern</p>
                        <p className="text-sm text-gray-600">Nur ausgewählte Benutzer haben Zugriff</p>
                      </div>
                    </label>

                    {/* Eingabe für bestimmte Benutzer */}
                    <div className="mt-3 ml-6 space-y-2">
                      {sharedWith.map((user, index) => (
                        <div key={index} className="flex items-center gap-2">
                          <Input
                            type="text"
                            placeholder="Benutzername oder E-Mail"
                            value={user}
                            onChange={(e) => {
                              const updated = [...sharedWith];
                              updated[index] = e.target.value;
                              setSharedWith(updated);
                            }}
                          />
                          <Button
                            variant="ghost"
                            onClick={() => {
                              setSharedWith(sharedWith.filter((_, i) => i !== index));
                            }}
                          >
                            Entfernen
                          </Button>
                        </div>
                      ))}

                      <Button
                        type="button"
                        variant="outline"
                        onClick={() => setSharedWith([...sharedWith, ""])}
                      >
                        + Benutzer hinzufügen
                      </Button>
                    </div>
                  </div>

                </div>
                
              </div>
              <Button 
                data-testid="submit-board-button" 
                onClick={createBoard} 
                className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
              >
                Board erstellen
              </Button>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
          </div>
        ) : boards.length === 0 ? (
          <Card className="p-12 text-center border-2 border-dashed">
            <Columns3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-gray-900 mb-2">Keine Boards gefunden</h3>
            <p className="text-gray-600 mb-6">Erstellen Sie Ihr erstes Board, um loszulegen</p>
            <Button onClick={() => setShowDialog(true)} className="gap-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700">
              <Plus className="w-4 h-4" />
              Erstes Board erstellen
            </Button>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="boards-grid">
            {boards.map((board) => (
              <Card
                key={board.id}
                data-testid={`board-card-${board.id}`}
                className="group hover:shadow-xl transition-all duration-300 cursor-pointer border-0 bg-white/80 backdrop-blur-sm overflow-hidden"
              >
                <div className="h-2 bg-gradient-to-r from-blue-500 to-purple-600"></div>
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1" onClick={() => navigate(`/board/${board.id}`)}>
                      <CardTitle className="text-xl group-hover:text-blue-600 transition-colors">
                        {board.name}
                      </CardTitle>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {board.isShared && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Für alle
                          </span>
                        )}
                        {!board.isShared && board.sharedWith && board.sharedWith.length > 0 && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            Geteilt mit {board.sharedWith.length}
                          </span>
                        )}
                        {!board.isShared && (!board.sharedWith || board.sharedWith.length === 0) && (
                          <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                            Privat
                          </span>
                        )}
                      </div>
                    </div>
                    {board.ownerId === user.id && (
                      <Button
                        data-testid={`edit-board-${board.id}`}
                        variant="ghost"
                        size="icon"
                        className="opacity-0 group-hover:opacity-100 transition-opacity"
                        onClick={(e) => openEditDialog(board, e)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                </CardHeader>
                <CardContent onClick={() => navigate(`/board/${board.id}`)}>
                  <p className="text-sm text-gray-600">
                    Erstellt am {new Date(board.createdAt).toLocaleDateString('de-DE')}
                  </p>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </main>

      {/* Edit Board Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent data-testid="edit-board-dialog" className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Board bearbeiten</DialogTitle>
            <DialogDescription>
              Ändern Sie die Einstellungen für dieses Board
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="edit-board-name">Board-Name</Label>
              <Input
                id="edit-board-name"
                data-testid="edit-board-name-input"
                placeholder="z.B. Projekt X"
                value={boardName}
                onChange={(e) => setBoardName(e.target.value)}
              />
            </div>
            
            <div className="space-y-3">
              <Label>Sichtbarkeit</Label>
              
              {/* Private */}
              <div
                className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  !isShared && sharedWith.length === 0
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => {
                  setIsShared(false);
                  setSharedWith([]);
                }}
              >
                <input
                  type="radio"
                  name="edit-sharing"
                  checked={!isShared && sharedWith.length === 0}
                  onChange={() => {
                    setIsShared(false);
                    setSharedWith([]);
                  }}
                  className="mt-1 mr-3"
                />
                <div>
                  <p className="font-medium text-gray-900">Nur für mich</p>
                  <p className="text-sm text-gray-600">
                    Nur Sie können dieses Board sehen und bearbeiten
                  </p>
                </div>
              </div>

              {/* All Users */}
              <div
                className={`flex items-start p-3 border-2 rounded-lg cursor-pointer transition-all ${
                  isShared
                    ? "border-blue-500 bg-blue-50"
                    : "border-gray-200 hover:border-gray-300"
                }`}
                onClick={() => {
                  setIsShared(true);
                  setSharedWith([]);
                }}
              >
                <input
                  type="radio"
                  name="edit-sharing"
                  checked={isShared}
                  onChange={() => {
                    setIsShared(true);
                    setSharedWith([]);
                  }}
                  className="mt-1 mr-3"
                />
                <div>
                  <p className="font-medium text-gray-900">Für alle Benutzer</p>
                  <p className="text-sm text-gray-600">
                    Alle Benutzer können dieses Board sehen und bearbeiten
                  </p>
                </div>
              </div>
            </div>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={closeDialogs} className="flex-1">
              Abbrechen
            </Button>
            <Button 
              data-testid="update-board-button" 
              onClick={updateBoard} 
              className="flex-1 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700"
            >
              Änderungen speichern
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
